import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, asArray, formatApiError } from "@/lib/api";
import { pesoOr } from "@/lib/money";
import {
  AlertTriangle, ArrowLeft, CheckCircle2, Download, FileSpreadsheet, Plus,
  RefreshCw, Undo2,
} from "lucide-react";

// Billing runs: the list, and the one screen a person reads before approving a
// figure a client will be sent.
//
// ⚠️ **THE POINT OF THE DETAIL SCREEN IS WHAT IS *NOT* ON THE RUN.** Task 6
// computed `contract_charges_omitted`, `unevaluated_count` and `notes_count`
// honestly, wrote them onto the run document and into the audit row, and they
// reached nobody; Task 7 added the route that returns them WITH the rows
// behind each one. This is the reader. A run that quietly bills less than the
// work done is the defect this whole sub-project exists to prevent, and a
// number nobody renders is a number nobody sees -- this sub-project has
// produced that shape three times already.
//
// ⚠️ **NO MONEY ARITHMETIC HERE.** `total_minor` is the server's figure and
// this page prints it. It does not sum the lines to check the total: a second
// computation that disagrees with the record is a bug generator, and the
// server already refuses to approve or export a run whose stored total does
// not match its lines (`billing._agreement_or_409`).
//
// ⚠️ **THE BUTTONS ARE NOT THE GUARD.** `approve` accepts only a draft,
// `export` only an approved or exported run, `release` only a draft or one
// part-way through releasing. Those refusals are the control. Hiding a button
// the state forbids only stops this screen offering a dead action.

const STATE_TONE = {
  draft: "bg-zinc-800 text-zinc-300 border-zinc-700",
  releasing: "bg-violet-500/10 text-violet-300 border-violet-500/30",
  approved: "bg-blue-500/10 text-blue-300 border-blue-500/30",
  exported: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
};

// `billing.RELEASABLE`. Kept as a named set rather than a literal "draft" at
// each call site, for the reason `billing._remedy` gives for keying on the
// same tuple: a message and a button that disagree with the route they name
// send an operator down a dead end.
const RELEASABLE = ["draft", "releasing"];

// How many rows of an uncapped list this screen draws before it stops and says
// how many there are. `excluded` and `contested` are stored UNCAPPED on the run
// and the detail route hands them over whole, so a run with hundreds of
// exclusions would otherwise put hundreds of rows on one page.
const PAGE = 25;

// Did this run leave anything out? Asked once and answered from the COUNTS the
// server stored, so the three places that speak about an empty run cannot
// disagree with each other.
//
// ⚠️ AN EMPTY RUN HAS TWO COMPLETELY DIFFERENT MEANINGS AND THEY MUST NOT
// SHARE A SENTENCE. A run with no lines and nothing left out is a month in
// which nobody logged chargeable work and no charge was due: the bill really
// is nothing. A run with no lines and a hundred exclusions is the production
// shape -- the work exists and none of it could be billed. Telling an operator
// to "see what was left out" on the first one sends them to a section that
// says nothing was left out, and calling the second one a quiet month hides
// the whole problem.
function leftOutCount(run) {
  return (run.excluded_count || 0) + (run.contested_count || 0)
    + (run.contract_charges_omitted || 0) + (run.unevaluated_count || 0)
    + (run.notes_count || 0);
}

function StateBadge({ state }) {
  const cls = STATE_TONE[state] || "bg-zinc-800 text-zinc-300 border-zinc-700";
  return (
    <span data-testid={`run-state-${state}`}
      className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-mono font-medium uppercase tracking-wider border ${cls}`}>
      {state || "unknown"}
    </span>
  );
}

function plural(n, one, many) {
  return `${n} ${n === 1 ? one : many}`;
}

function fmtDate(ts) {
  if (!ts) return null;
  try { return new Date(ts).toLocaleString(); } catch { return String(ts); }
}

function fmtMinutes(m) {
  if (m === null || m === undefined) return null;
  const n = Number(m) || 0;
  const h = Math.floor(n / 60);
  const r = n % 60;
  return h ? `${h}h ${r}m` : `${r} min`;
}

// ⚠️ THE EXPORT ROUTE ANSWERS WITH A CSV, SO ITS REFUSALS ARRIVE AS A BLOB
// TOO. `formatApiError` reads `e.response.data.detail`, and on a
// `responseType: "blob"` request `data` is a Blob whose `.detail` is
// undefined -- so every 409 from the export would have read "Something went
// wrong" and the server's carefully-worded remedy would never have reached
// the person who needed it. This reads the blob back as text, parses it, and
// puts the real body where `formatApiError` looks.
async function formatBlobError(e) {
  const data = e?.response?.data;
  if (data instanceof Blob) {
    try {
      const text = await data.text();
      const parsed = JSON.parse(text);
      return formatApiError({ ...e, response: { ...e.response, data: parsed } });
    } catch {
      return formatApiError(e);
    }
  }
  return formatApiError(e);
}

export default function BillingRuns() {
  const { runId } = useParams();
  return runId ? <RunDetail runId={runId} /> : <RunList />;
}

// --------------------------------------------------------------------------- //
// The list.
// --------------------------------------------------------------------------- //
function RunList() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [clients, setClients] = useState([]);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  // The create banner is DURABLE: a toast that has faded takes the server's
  // sentence with it, and the sentence is the whole answer on this screen.
  const [banner, setBanner] = useState(null);

  const clientId = params.get("client_id") || "";
  const period = params.get("period") || "";
  const state = params.get("state") || "";

  // Prefilled from the query string so the Unbilled Work screen can send an
  // operator here with the client and month already chosen -- it links, it
  // never creates. See UnbilledWork.jsx.
  const [newClient, setNewClient] = useState(params.get("client_id") || "");
  const [newPeriod, setNewPeriod] = useState(params.get("period") || "");

  const load = useCallback(() => {
    setLoading(true);
    const q = new URLSearchParams();
    if (clientId) q.set("client_id", clientId);
    if (period) q.set("period", period);
    if (state) q.set("state", state);
    const qs = q.toString();
    api.get(`/billing/runs${qs ? `?${qs}` : ""}`)
      .then((r) => { setData(r.data); setErr(""); })
      .catch((e) => { setData(null); setErr(formatApiError(e)); })
      .finally(() => setLoading(false));
  }, [clientId, period, state]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get("/clients").then((r) => setClients(asArray(r.data)))
      .catch(() => setClients([]));
  }, []);

  const setFilter = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    setParams(next, { replace: true });
  };

  const create = async () => {
    setCreating(true);
    setBanner(null);
    try {
      const { data: run } = await api.post("/billing/runs", {
        client_id: newClient, period: newPeriod.trim(),
      });
      toast.success(`Draft run created for ${run.client_name || run.client_id}, ${run.period}`);
      navigate(`/billing/runs/${run.id}`);
    } catch (e) {
      setBanner({ tone: "bad", msg: formatApiError(e) });
    } finally {
      setCreating(false);
    }
  };

  const runs = asArray(data?.runs);
  const clientName = (id) => clients.find((c) => c.id === id)?.name || id;

  return (
    <div className="space-y-4" data-testid="billing-runs-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono">Billing · Preparation</div>
          <h1 className="font-display text-3xl font-semibold tracking-tight mt-1">Billing Runs</h1>
          <div className="text-xs text-zinc-500 mt-1">
            One run is one client and one calendar month. This is billing preparation, not an invoice: nothing here carries an invoice number.
          </div>
        </div>
        <button onClick={load} data-testid="billing-runs-refresh"
          className="border border-zinc-800 rounded-md px-3 py-1.5 text-sm text-zinc-300 hover:bg-zinc-900 flex items-center gap-1.5">
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {banner && (
        <div data-testid="billing-runs-banner"
          className="border border-red-900/50 bg-red-950/30 text-red-300 rounded px-4 py-3 text-sm whitespace-pre-wrap">
          {banner.msg}
        </div>
      )}

      {/* Create. The server validates the period (YYYY-MM) and the client, and
          refuses a second run for a client-month the unique index already
          holds. Those refusals land in the durable banner above verbatim. */}
      <div className="bg-zinc-950 border border-zinc-900 rounded-md p-4 space-y-3" data-testid="billing-run-create">
        <div className="text-sm font-medium text-zinc-200 flex items-center gap-2">
          <Plus size={15} className="text-zinc-500" /> Create a draft run
        </div>
        <div className="flex flex-wrap gap-3 items-end">
          <label className="text-xs text-zinc-500 space-y-1">
            <div>Client</div>
            <select value={newClient} onChange={(e) => setNewClient(e.target.value)}
              data-testid="billing-run-create-client"
              className="bg-zinc-900 border border-zinc-800 rounded px-2 py-1.5 text-sm text-zinc-200 min-w-[16rem]">
              <option value="">Choose a client</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </label>
          <label className="text-xs text-zinc-500 space-y-1">
            <div>Period (YYYY-MM)</div>
            <input value={newPeriod} onChange={(e) => setNewPeriod(e.target.value)}
              placeholder="2026-09" data-testid="billing-run-create-period"
              className="bg-zinc-900 border border-zinc-800 rounded px-2 py-1.5 text-sm text-zinc-200 font-mono w-32" />
          </label>
          <button onClick={create} disabled={creating || !newClient || !newPeriod.trim()}
            data-testid="billing-run-create-submit"
            className="bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white rounded px-3 py-1.5 text-sm flex items-center gap-1.5">
            <Plus size={14} /> {creating ? "Creating…" : "Create draft"}
          </button>
        </div>
        <div className="text-[11px] text-zinc-600 leading-relaxed">
          Creating a run CLAIMS that client's approved billable work for the month, so work logged afterwards cannot join it.
          A run for a month that has not happened yet still bills recurring contract charges in full, and says so on its own detail screen.
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-end" data-testid="billing-runs-filters">
        <label className="text-xs text-zinc-500 space-y-1">
          <div>Client</div>
          <select value={clientId} onChange={(e) => setFilter("client_id", e.target.value)}
            data-testid="billing-runs-filter-client"
            className="bg-zinc-900 border border-zinc-800 rounded px-2 py-1.5 text-sm text-zinc-200 min-w-[14rem]">
            <option value="">Every client</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </label>
        <label className="text-xs text-zinc-500 space-y-1">
          <div>Period</div>
          <input value={period} onChange={(e) => setFilter("period", e.target.value)}
            placeholder="2026-09" data-testid="billing-runs-filter-period"
            className="bg-zinc-900 border border-zinc-800 rounded px-2 py-1.5 text-sm text-zinc-200 font-mono w-32" />
        </label>
        <label className="text-xs text-zinc-500 space-y-1">
          <div>State</div>
          <select value={state} onChange={(e) => setFilter("state", e.target.value)}
            data-testid="billing-runs-filter-state"
            className="bg-zinc-900 border border-zinc-800 rounded px-2 py-1.5 text-sm text-zinc-200">
            <option value="">Every state</option>
            <option value="draft">Draft</option>
            <option value="releasing">Releasing</option>
            <option value="approved">Approved</option>
            <option value="exported">Exported</option>
          </select>
        </label>
      </div>

      {err && <div className="border border-red-900/50 bg-red-950/30 text-red-300 rounded px-4 py-3 text-sm" data-testid="billing-runs-error">{err}</div>}
      {loading && !err && <div className="text-zinc-600 text-sm">Loading…</div>}

      {/* ⚠️ A TRUNCATED LIST IS SAID, NEVER INFERRED FROM ITS LENGTH. The
          route names `truncated` and `limit` for exactly this. */}
      {data?.truncated && (
        <div className="border border-amber-500/40 bg-amber-950/20 text-amber-300 rounded px-4 py-3 text-sm" data-testid="billing-runs-truncated">
          This is the first {data.limit} runs and there are more. Narrow it with the filters above, because anything below the cut is not on this page.
        </div>
      )}

      {!loading && !err && !runs.length && (
        <div className="bg-zinc-950 border border-zinc-900 rounded-md px-4 py-8 text-zinc-500 text-sm" data-testid="billing-runs-empty">
          {clientId || period || state
            ? "No billing run matches these filters. Clear them to see every run."
            : "No billing run has been created yet. Choose a client and a month above to prepare one, or start from Unbilled Work to see what is waiting."}
        </div>
      )}

      {!!runs.length && (
        <div className="bg-zinc-950 border border-zinc-900 rounded-md overflow-hidden" data-testid="billing-runs-table">
          <table className="w-full text-sm hidden md:table">
            <thead className="bg-zinc-900/50 border-b border-zinc-900 text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
              <tr>
                <th className="text-left px-4 py-3">Client</th>
                <th className="text-left px-4 py-3">Period</th>
                <th className="text-left px-4 py-3">State</th>
                <th className="text-left px-4 py-3">Lines</th>
                <th className="text-left px-4 py-3">Total</th>
                <th className="text-left px-4 py-3">Left out</th>
                <th className="text-left px-4 py-3">Created</th>
              </tr>
            </thead>
            <tbody>
              {runs.map((r) => (
                <tr key={r.id} data-testid={`billing-run-row-${r.id}`} className="border-b border-zinc-900 hover:bg-zinc-900/40">
                  <td className="px-4 py-2">
                    <Link to={`/billing/runs/${r.id}`} className="text-zinc-200 hover:text-blue-400">
                      {r.client_name || clientName(r.client_id)}
                    </Link>
                  </td>
                  <td className="px-4 py-2 font-mono text-zinc-300">{r.period}</td>
                  <td className="px-4 py-2"><StateBadge state={r.state} /></td>
                  <td className="px-4 py-2 font-mono text-zinc-400">{r.line_count ?? 0}</td>
                  <td className="px-4 py-2 font-mono"><TotalCell run={r} /></td>
                  <td className="px-4 py-2 text-xs"><LeftOutSummary run={r} /></td>
                  <td className="px-4 py-2 text-xs text-zinc-500">{fmtDate(r.created_at) || "Not recorded"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="md:hidden divide-y divide-zinc-900">
            {runs.map((r) => (
              <div key={r.id} className="p-4 space-y-1.5" data-testid={`billing-run-card-${r.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <Link to={`/billing/runs/${r.id}`} className="text-zinc-100 font-medium hover:text-blue-400">
                    {r.client_name || clientName(r.client_id)}
                  </Link>
                  <StateBadge state={r.state} />
                </div>
                <div className="text-xs text-zinc-500 font-mono">{r.period} · {plural(r.line_count ?? 0, "line", "lines")}</div>
                <div className="text-sm font-mono"><TotalCell run={r} /></div>
                <div className="text-xs"><LeftOutSummary run={r} /></div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ⚠️ **A RUN THAT BILLS NOTHING MUST NOT SCAN LIKE ANY OTHER ZERO.** This is
// the screen somebody reads down, and a plain "P0.00" in the same weight as
// "P559,600.00" is a figure the eye slides over: on production every unbilled
// entry is unpriced, so a real run there is a zero, and the whole point of this
// sub-project is that it must not look like a quiet month. The amount is toned
// and carries the consequence in words, and the "left out" column beside it
// says which of the two opposite meanings this zero has.
function TotalCell({ run }) {
  const empty = !run.line_count;
  return (
    <span data-testid={`run-total-${run.id}`}>
      <span className={empty ? "text-amber-300" : "text-zinc-100"}>
        {pesoOr(run.total_minor, "No total recorded")}
      </span>
      <span className="text-zinc-600 ml-1.5 text-xs">{run.currency}</span>
      {empty && (
        <span className="block text-[11px] text-amber-300/80 font-sans" data-testid={`run-bills-nothing-${run.id}`}>
          Bills nothing
        </span>
      )}
    </span>
  );
}

// One line saying whether anything was left off this run. The list projection
// carries the COUNTS and not the rows (`billing._LIST_PROJECTION`), so this
// says how many and the detail screen says which.
function LeftOutSummary({ run }) {
  const bits = [];
  if (run.excluded_count) bits.push(plural(run.excluded_count, "entry excluded", "entries excluded"));
  if (run.contested_count) bits.push(plural(run.contested_count, "entry contested", "entries contested"));
  if (run.contract_charges_omitted) bits.push(`${plural(run.contract_charges_omitted, "contract charge", "contract charges")} omitted`);
  if (run.unevaluated_count) bits.push(`${plural(run.unevaluated_count, "charge", "charges")} unevaluated`);
  if (run.notes_count) bits.push(plural(run.notes_count, "note", "notes"));
  // ⚠️ THE SAME TWO OPPOSITE MEANINGS THE DETAIL SCREEN SEPARATES, separated
  // here too. "Nothing was left out" on a run that also billed nothing reads as
  // an all-clear, and on production it would be one: the work is there and none
  // of it could be billed, which is the other row entirely.
  if (!bits.length) {
    return run.line_count
      ? <span className="text-zinc-600" data-testid={`run-leftout-none-${run.id}`}>Nothing was left out</span>
      : <span className="text-zinc-500" data-testid={`run-leftout-empty-month-${run.id}`}>Nothing was left out, and there was nothing to bill</span>;
  }
  return (
    <span className="text-amber-300" data-testid={`run-leftout-${run.id}`}>
      {run.line_count ? bits.join(", ") : `none of it billable: ${bits.join(", ")}`}
    </span>
  );
}

// --------------------------------------------------------------------------- //
// The detail.
// --------------------------------------------------------------------------- //
function RunDetail({ runId }) {
  const navigate = useNavigate();
  const [run, setRun] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  // ⚠️ DURABLE, not a toast. Every refusal this screen can meet is a
  // paragraph naming what to do next, and a toast takes it away after four
  // seconds. It stays until the next action replaces it.
  const [banner, setBanner] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/billing/runs/${runId}`)
      .then((r) => { setRun(r.data); setErr(""); })
      .catch((e) => { setRun(null); setErr(formatApiError(e)); })
      .finally(() => setLoading(false));
  }, [runId]);
  useEffect(() => { load(); }, [load]);

  const approve = async () => {
    setBusy("approve"); setBanner(null);
    try {
      await api.post(`/billing/runs/${runId}/approve`);
      setBanner({ tone: "good", msg: "Approved. The figures are now frozen: the next step is the export, and a run past draft can no longer be released." });
      load();
    } catch (e) {
      setBanner({ tone: "bad", msg: formatApiError(e) });
    } finally { setBusy(""); }
  };

  const exportRun = async () => {
    setBusy("export"); setBanner(null);
    try {
      const res = await api.post(`/billing/runs/${runId}/export`, null, { responseType: "blob" });
      // The filename and the sha256 come from the response headers the route
      // sets, never invented here.
      const disp = String(res.headers?.["content-disposition"] || "");
      const match = /filename="([^"]+)"/.exec(disp);
      const name = match ? match[1] : `billing-prep-${runId}.csv`;
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = name;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      const sha = res.headers?.["x-export-sha256"];
      setBanner({
        tone: "good",
        msg: `Exported ${name}.${sha ? ` sha256 ${sha}` : ""} Exporting again re-renders the same lines and refuses if the bytes have changed, so this hash is how a copy is checked.`,
      });
      load();
    } catch (e) {
      setBanner({ tone: "bad", msg: await formatBlobError(e) });
    } finally { setBusy(""); }
  };

  const release = async () => {
    const ok = window.confirm(
      "Release this run?\n\nIt gives the month's work back so it can be billed again, DELETES this draft and its lines, and cannot be undone. " +
      "It is the only in-product way to correct a run, and it stops working the moment the run is approved.");
    if (!ok) return;
    setBusy("release"); setBanner(null);
    try {
      const { data } = await api.post(`/billing/runs/${runId}/release`);
      toast.success("Run released, the month's work is billable again");
      navigate("/billing/runs", { replace: true, state: { released: data?.id } });
    } catch (e) {
      setBanner({ tone: "bad", msg: formatApiError(e) });
      setBusy("");
    }
  };

  if (loading) return <div className="text-zinc-600 text-sm" data-testid="billing-run-loading">Loading…</div>;
  if (err) {
    return (
      <div className="space-y-4" data-testid="billing-run-detail-error">
        <Link to="/billing/runs" className="text-sm text-zinc-400 hover:text-blue-400 flex items-center gap-1.5">
          <ArrowLeft size={14} /> Billing runs
        </Link>
        <div className="border border-red-900/50 bg-red-950/30 text-red-300 rounded px-4 py-3 text-sm">{err}</div>
      </div>
    );
  }
  if (!run) return null;

  const state = run.state;
  // The server decides; these only stop the screen offering a dead action.
  const canApprove = state === "draft";
  const canExport = state === "approved" || state === "exported";
  const canRelease = RELEASABLE.includes(state);

  return (
    <div className="space-y-4" data-testid="billing-run-detail">
      <Link to="/billing/runs" className="text-sm text-zinc-400 hover:text-blue-400 inline-flex items-center gap-1.5">
        <ArrowLeft size={14} /> Billing runs
      </Link>

      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono">Billing · Preparation</div>
          <h1 className="font-display text-3xl font-semibold tracking-tight mt-1">
            {run.client_name || run.client_id} · {run.period}
          </h1>
          <div className="text-xs text-zinc-500 mt-1 flex items-center gap-2 flex-wrap">
            <StateBadge state={state} />
            <span className="font-mono">{run.id}</span>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={load} data-testid="billing-run-refresh"
            className="border border-zinc-800 rounded-md px-3 py-1.5 text-sm text-zinc-300 hover:bg-zinc-900 flex items-center gap-1.5">
            <RefreshCw size={14} /> Refresh
          </button>
          {canRelease && (
            <button onClick={release} disabled={!!busy} data-testid="billing-run-release"
              className="border border-amber-700/60 text-amber-300 hover:bg-amber-950/40 disabled:opacity-40 rounded-md px-3 py-1.5 text-sm flex items-center gap-1.5">
              <Undo2 size={14} /> {busy === "release" ? "Releasing…" : "Release"}
            </button>
          )}
          {canApprove && (
            <button onClick={approve} disabled={!!busy} data-testid="billing-run-approve"
              className="bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white rounded-md px-3 py-1.5 text-sm flex items-center gap-1.5">
              <CheckCircle2 size={14} /> {busy === "approve" ? "Approving…" : "Approve"}
            </button>
          )}
          {canExport && (
            <button onClick={exportRun} disabled={!!busy} data-testid="billing-run-export"
              className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white rounded-md px-3 py-1.5 text-sm flex items-center gap-1.5">
              <Download size={14} /> {busy === "export" ? "Exporting…" : state === "exported" ? "Download again" : "Export CSV"}
            </button>
          )}
        </div>
      </div>

      {banner && (
        <div data-testid="billing-run-banner"
          className={`rounded px-4 py-3 text-sm whitespace-pre-wrap border ${
            banner.tone === "good"
              ? "border-emerald-900/50 bg-emerald-950/30 text-emerald-300"
              : "border-red-900/50 bg-red-950/30 text-red-300"}`}>
          {banner.msg}
        </div>
      )}

      {/* What this screen can and cannot do, in the state it is in. Written
          here rather than left to be discovered from a refusal: an approved or
          exported run has NO in-product correction, and saying so before
          somebody presses Approve is the only moment it helps. */}
      <StateExplainer state={state} run={run} />

      {/* The money. The server's figures, printed. */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="billing-run-totals">
        <Stat label="Total" value={pesoOr(run.total_minor, "No total recorded")} sub={run.currency} tone="strong" />
        <Stat label="Lines" value={String(run.line_count ?? 0)}
          sub={`${run.time_line_count ?? 0} time, ${Math.max(0, (run.line_count ?? 0) - (run.time_line_count ?? 0))} contract`} />
        <Stat label="Contract charges billed" value={String(run.contract_charges_billed ?? 0)}
          sub={run.contract_charges_omitted
            ? `${run.contract_charges_omitted} more ${run.contract_charges_omitted === 1 ? "was" : "were"} due and ${run.contract_charges_omitted === 1 ? "is" : "are"} not on this run`
            : "None were due and left off"} />
        <Stat label="Created" value={fmtDate(run.created_at) || "Not recorded"} sub={run.created_by || ""} small />
      </div>

      {(run.approved_at || run.exported_at) && (
        <div className="text-[11px] text-zinc-500 font-mono space-x-4" data-testid="billing-run-chain">
          {run.approved_at && <span>Approved {fmtDate(run.approved_at)} by {run.approved_by || "unrecorded"}</span>}
          {run.exported_at && <span>Exported {fmtDate(run.exported_at)} by {run.exported_by || "unrecorded"}</span>}
          {run.export_sha256 && <span>sha256 {run.export_sha256}</span>}
        </div>
      )}

      {/* ⚠️ THE SECTION THIS PAGE EXISTS FOR. */}
      <LeftOut run={run} />

      <Lines lines={asArray(run.lines)} currency={run.currency} leftOut={leftOutCount(run)} />
    </div>
  );
}

function Stat({ label, value, sub, tone, small }) {
  return (
    <div className="bg-zinc-950 border border-zinc-900 rounded-md p-4">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">{label}</div>
      <div className={`${small ? "text-sm mt-2" : "text-2xl mt-1"} font-display ${tone === "strong" ? "text-zinc-50" : "text-zinc-200"}`}>{value}</div>
      {sub ? <div className="text-xs text-zinc-500 mt-1">{sub}</div> : null}
    </div>
  );
}

function StateExplainer({ state, run }) {
  // ⚠️ EVERY SENTENCE HERE NAMES ONLY A REMEDY THAT EXISTS. This sub-project
  // has shipped a message pointing at an unreachable route three times ("will
  // be billed once it is priced", a merge message offering a later period,
  // and "release it and create it again" on a run release refuses). An
  // approved or exported run cannot be released and has no in-product
  // correction, and this says exactly that rather than inventing one.
  const map = {
    draft: "This is a draft. Approving it freezes the figures. Releasing it gives the month's work back and deletes the run, which is the only way to correct it, and it stops working the moment it is approved.",
    releasing: "This run is part way through being released. Its work has been given back or is being given back, so there is nothing here to approve or export. Press Release again to finish the job, then create the run afresh.",
    approved: "Approved, so the figures are frozen. It cannot be released any more and there is no in-product correction for a run in this state: a wrong figure now needs somebody with database access and a record of why. The next step is the export.",
    exported: "Exported. Exporting again re-renders the same lines and hands back the same file; if the bytes have changed since, it refuses and names both hashes rather than issuing a second, different document under one run id. It cannot be released or corrected in-product.",
  };
  const msg = map[state];
  if (!msg) {
    return (
      <div className="border border-amber-500/40 bg-amber-950/20 text-amber-300 rounded px-4 py-3 text-sm" data-testid="billing-run-state-unknown">
        This run is in a state this screen does not recognise ({String(state)}). Approve, export and release will each refuse it, and the refusal will say why.
      </div>
    );
  }
  return (
    <div className="border border-zinc-900 bg-zinc-950 rounded px-4 py-3 text-xs text-zinc-400 leading-relaxed" data-testid="billing-run-state-note">
      {msg}
      {run.line_count === 0 && (
        <span className="block mt-2 text-amber-300" data-testid="billing-run-empty-note">
          {leftOutCount(run)
            ? "This run holds no lines, so there is nothing to bill and Approve will refuse it. The work exists: every row of it is listed below, with why none of it could be billed."
            : "This run holds no lines, so there is nothing to bill and Approve will refuse it. Nothing was left out either, so this month genuinely had no chargeable work and no contract charge due. Release it if it was created by mistake, because it is holding this client's month closed for nothing."}
        </span>
      )}
    </div>
  );
}

// --------------------------------------------------------------------------- //
// What was left out, in words, with the rows behind every count.
// --------------------------------------------------------------------------- //
function LeftOut({ run }) {
  const excluded = asArray(run.excluded);
  const contested = asArray(run.contested);
  const notes = asArray(run.notes);
  const unevaluated = asArray(run.unevaluated);
  const omitted = asArray(run.omitted_charges);

  const total = excluded.length + contested.length + notes.length
    + unevaluated.length + omitted.length;

  // The counts the SERVER stored, printed beside the rows they count, so the
  // two can be compared by the person reading rather than trusted.
  const headline = useMemo(() => {
    const bits = [];
    if (run.excluded_count) {
      bits.push(`${plural(run.excluded_count, "time entry was", "time entries were")} excluded from this run`
        + (run.unpriced_entries ? ` (${run.unpriced_entries} because no amount was recorded)` : ""));
    }
    if (run.contested_count) bits.push(`${plural(run.contested_count, "entry", "entries")} changed underneath the run and could not be claimed`);
    if (run.contract_charges_omitted) {
      bits.push(run.contract_charges_omitted === 1
        ? "1 contract charge was due this month and is not on this run"
        : `${run.contract_charges_omitted} contract charges were due this month and are not on this run`);
    }
    if (run.unevaluated_count) bits.push(`${plural(run.unevaluated_count, "contract charge", "contract charges")} could not be evaluated at all`);
    if (run.notes_count) bits.push(`${plural(run.notes_count, "note needs", "notes need")} reading before this is approved`);
    return bits;
  }, [run]);

  if (!headline.length && !total) {
    return (
      <div className="bg-zinc-950 border border-zinc-900 rounded-md px-4 py-4 text-sm text-zinc-400" data-testid="billing-run-leftout-clean">
        <span className="text-emerald-400 font-medium">Nothing was left out of this run.</span>{" "}
        No entry was excluded, none was contested, no contract charge was omitted or left unevaluated, and there are no notes to read.{" "}
        {run.line_count
          ? "Every approved, billable entry in this month and every contract charge due in it is a line below."
          : "There were no lines either, so this month held no approved, billable work for this client and no contract charge fell due in it. The bill really is nothing; nothing was filtered out to make it look that way."}
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="billing-run-leftout">
      <div className="border border-amber-500/40 bg-amber-950/20 rounded-md p-4 space-y-2">
        <div className="flex items-center gap-2 text-amber-300 font-medium text-sm">
          <AlertTriangle size={16} /> What this run does not bill
        </div>
        <ul className="text-sm text-amber-200/90 list-disc pl-5 space-y-1" data-testid="billing-run-leftout-headline">
          {headline.map((b, i) => <li key={i}>{b}</li>)}
        </ul>
        {/* ⚠️ THE TENSE FOLLOWS THE STATE. "Read every row before approving"
            printed on an already-approved run is an instruction nobody can
            act on, which is the same defect as naming a route that refuses. */}
        <div className="text-[11px] text-amber-300/70 leading-relaxed" data-testid="billing-run-leftout-advice">
          {run.state === "draft"
            ? "Read every row below before approving. Once this run is approved the figures are frozen, it can no longer be released, and this month is closed to the work listed here: no route adds a line to an existing run and a second run for this client and period is refused."
            : "This month is now closed to the work listed here, and this run can no longer be released. No route adds a line to an existing run and a second run for this client and period is refused, so putting any of it right needs somebody with database access and a record of why."}
        </div>
      </div>

      {/* ⚠️ `excluded` MIXES TIME ENTRIES AND CONTRACT CHARGES -- `_line` and
          `_charge_excluded` build deliberately identical key sets so one table
          can render both without a dash in three columns on every row. A charge
          that lands here therefore appears AGAIN under "due and not billed"
          below, which is the same row seen from the charge's side rather than a
          second problem. Said in words, because two counts that overlap and do
          not say so read as double the trouble. */}
      <RowBlock
        testid="leftout-excluded"
        title="Excluded from this run"
        note={excluded.some((x) => x.charge_id)
          ? "This list holds time entries and contract charges together. A contract charge here is counted again under “due and not billed” below, which is the same row seen from the charge's side, not a second problem."
          : null}
        count={run.excluded_count ?? excluded.length}
        rows={excluded}
        emptyWhenCounted="The run records a count but returned no rows for it. That disagreement is itself the finding: run the billing reconciliation."
        render={(x, o) => (
          <>
            <div className="text-zinc-200">
              {x.description || (x.entry_id ? `Time entry ${x.entry_id}` : x.charge_id || "Unnamed row")}
              <span className="ml-2 text-[10px] uppercase font-mono tracking-wider text-amber-300">{String(x.reason || "no reason recorded").replaceAll("_", " ")}</span>
            </div>
            <div className="text-[11px] text-zinc-500 font-mono space-x-2">
              {x.occurred_on && <span>{x.occurred_on}</span>}
              {fmtMinutes(x.minutes) && <span>{fmtMinutes(x.minutes)}</span>}
              {x.ticket_id && <Link to={`/tickets/${x.ticket_id}`} className="text-zinc-400 hover:text-blue-400">open the ticket</Link>}
              {x.charge_id && <span>charge {x.charge_id}</span>}
              {!x.occurred_on && !x.ticket_id && !x.charge_id && <span>No date, ticket or charge recorded</span>}
            </div>
            {!o?.hideDetail && (
              <div className="text-xs text-zinc-400 leading-relaxed">{x.detail || "No explanation was recorded for this exclusion."}</div>
            )}
          </>
        )}
      />

      <RowBlock
        testid="leftout-contested"
        title="Contested work"
        count={run.contested_count ?? contested.length}
        rows={contested}
        emptyWhenCounted="The run records a count but returned no rows for it. Run the billing reconciliation."
        render={(x, o) => (
          <>
            <div className="text-zinc-200">
              Time entry {x.entry_id}
              <span className="ml-2 text-[10px] uppercase font-mono tracking-wider text-amber-300">{String(x.reason || "unknown").replaceAll("_", " ")}</span>
            </div>
            <div className="text-[11px] text-zinc-500 font-mono">
              {x.ticket_id ? <Link to={`/tickets/${x.ticket_id}`} className="text-zinc-400 hover:text-blue-400">open the ticket</Link> : "No ticket recorded"}
            </div>
            {!o?.hideDetail && (
              <div className="text-xs text-zinc-400 leading-relaxed">{x.detail || "No explanation was recorded."}</div>
            )}
          </>
        )}
      />

      <RowBlock
        testid="leftout-omitted"
        title="Contract charges due and not billed"
        count={run.contract_charges_omitted ?? omitted.length}
        rows={omitted}
        emptyWhenCounted="The run counts omitted charges but no rows came back for them. Run the billing reconciliation."
        render={(x, o) => (
          <>
            <div className="text-zinc-200">
              {x.description || "Description not recorded"}
              {x.reason && <span className="ml-2 text-[10px] uppercase font-mono tracking-wider text-amber-300">{String(x.reason).replaceAll("_", " ")}</span>}
            </div>
            <div className="text-[11px] text-zinc-500 font-mono">
              {[x.charge_id, x.contract_id ? `contract ${x.contract_id}` : null,
                x.cadence, pesoOr(x.amount_minor, "no amount recorded")].filter(Boolean).join(" · ")}
            </div>
            {/* ⚠️ THE CHARGE ROW MAY BE GONE AND THAT IS NOT AN ERROR. An
                omitted charge has no billing line, so it is deletable, and the
                route says so per row rather than leaving a blank nobody can
                explain. */}
            {x.charge_exists === false && (
              <div className="text-[11px] text-zinc-500">This charge has since been deleted, so only what the run recorded about it is left.</div>
            )}
            {!o?.hideDetail && (
              <div className="text-xs text-zinc-400 leading-relaxed">{x.detail || "No explanation was recorded for this omission."}</div>
            )}
          </>
        )}
      />

      <RowBlock
        testid="leftout-unevaluated"
        title="Contract charges that could not be evaluated"
        count={run.unevaluated_count ?? unevaluated.length}
        rows={unevaluated}
        emptyWhenCounted="The run counts unevaluated charges but no rows came back for them. Run the billing reconciliation."
        render={(x, o) => (
          <>
            <div className="text-zinc-200">{x.description || "Description not recorded"}</div>
            <div className="text-[11px] text-zinc-500 font-mono">
              {[x.charge_id, x.contract_id ? `contract ${x.contract_id}` : null].filter(Boolean).join(" · ")}
            </div>
            <div className="text-xs text-zinc-400 leading-relaxed">
              {x.reason || "No reason was recorded."} Nothing decided whether this charge was due, so it is neither billed nor deliberately left off. Fix the charge and run the month again.
            </div>
          </>
        )}
      />

      <RowBlock
        testid="leftout-notes"
        title="Notes to read before approving"
        count={run.notes_count ?? notes.length}
        rows={notes}
        emptyWhenCounted="The run counts notes but none came back. Run the billing reconciliation."
        render={(x, o) => (
          <>
            <div className="text-zinc-200">
              {String(x.kind || "note").replaceAll("_", " ")}
              <span className={`ml-2 text-[10px] uppercase font-mono tracking-wider ${
                x.billed === true ? "text-emerald-400" : x.billed === false ? "text-amber-300" : "text-zinc-500"}`}>
                {x.billed === true ? "on this run" : x.billed === false ? "not on this run" : "about the run, not one charge"}
              </span>
            </div>
            <div className="text-[11px] text-zinc-500 font-mono">
              {[x.charge_id, x.contract_id ? `contract ${x.contract_id}` : null].filter(Boolean).join(" · ") || "Applies to the whole run"}
            </div>
            <div className="text-xs text-zinc-400 leading-relaxed">{x.detail || "No detail was recorded."}</div>
            {x.run_detail && <div className="text-xs text-zinc-400 leading-relaxed">{x.run_detail}</div>}
          </>
        )}
      />
    </div>
  );
}

// A count, said in words, above the rows behind it -- capped, because
// `excluded` and `contested` are stored UNCAPPED and the detail route returns
// them whole. The cap is NAMED rather than silent: a truncated list that says
// nothing looks complete.
function RowBlock({ testid, title, count, rows, render, emptyWhenCounted, note }) {
  const [showAll, setShowAll] = useState(false);
  const n = count ?? rows.length;

  // ⚠️ **ROWS ARE GROUPED BY THEIR EXPLANATION, AND EVERY GROUP IS ALWAYS ON
  // SCREEN.** The API's `detail` is per row and identical for every row with
  // the same reason, so 76 unpriced entries printed the same sixty-word
  // paragraph seventy-six times. The first fix printed it once -- but only
  // when EVERY row shared it, which is the all-or-nothing trap: a run with 300
  // `unpriced` exclusions and ONE `amount_not_an_integer` went straight back
  // to 300 identical blocks, burying the single row that said something
  // different. A partially-run migration is exactly how production becomes
  // that run.
  //
  // ⚠️ AND GROUPING ALONE WOULD NOT HAVE BEEN ENOUGH. With a flat cap of 25
  // over 301 rows, that one different row is still past the cut and still
  // invisible. So the cap is spent PER GROUP with a floor of one row each, and
  // every group's header -- its count and its explanation -- is drawn whatever
  // the cap does. The rare row is the one a person needs to see; it must not
  // compete for space with three hundred copies of the ordinary one.
  const groups = useMemo(() => {
    const m = new Map();
    rows.forEach((r) => {
      const k = r.detail || "";
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(r);
    });
    return Array.from(m, ([detail, groupRows]) => ({ detail, rows: groupRows }));
  }, [rows]);

  // How many rows of each group to draw. Every group gets one, then what is
  // left of the budget is spent in order.
  const allotted = useMemo(() => {
    if (showAll) return groups.map((g) => g.rows.length);
    let budget = Math.max(PAGE, groups.length);
    const take = groups.map(() => 0);
    groups.forEach((g, i) => { if (budget > 0) { take[i] = 1; budget -= 1; } });
    groups.forEach((g, i) => {
      const more = Math.min(g.rows.length - take[i], budget);
      if (more > 0) { take[i] += more; budget -= more; }
    });
    return take;
  }, [groups, showAll]);

  const drawn = allotted.reduce((a, b) => a + b, 0);
  const hidden = rows.length - drawn;

  if (!n && !rows.length) return null;

  return (
    <div className="bg-zinc-950 border border-zinc-900 rounded-md overflow-hidden" data-testid={testid}>
      <div className="px-4 py-2.5 border-b border-zinc-900 flex items-center justify-between gap-3 flex-wrap">
        <div className="text-sm text-zinc-200">
          {title}: <span className="text-amber-300">{n}</span>
          {groups.length > 1 && (
            <span className="text-zinc-500 text-xs ml-2">
              {groups.length} different reasons
            </span>
          )}
        </div>
        {hidden > 0 && (
          <div className="text-[11px] text-zinc-500">
            Showing {drawn} of {rows.length}.{" "}
            <button onClick={() => setShowAll(true)} data-testid={`${testid}-show-all`}
              className="text-blue-400 hover:underline">Show all {rows.length}</button>
          </div>
        )}
      </div>
      {note && <div className="px-4 py-2 text-[11px] text-zinc-500 border-b border-zinc-900 leading-relaxed">{note}</div>}
      {!rows.length ? (
        <div className="px-4 py-4 text-sm text-amber-300" data-testid={`${testid}-count-without-rows`}>{emptyWhenCounted}</div>
      ) : (
        groups.map((g, gi) => {
          const take = allotted[gi];
          // A group of ONE needs no header: its explanation goes on the row
          // itself, where a single row reads better than a heading over it.
          const grouped = g.rows.length > 1 && !!g.detail;
          return (
            <div key={`${testid}-g${gi}`} data-testid={`${testid}-group-${gi}`}
              className="border-b border-zinc-900 last:border-b-0">
              {grouped && (
                <div className="px-4 py-3 text-xs text-zinc-400 bg-zinc-900/30 leading-relaxed"
                  data-testid={`${testid}-group-detail-${gi}`}>
                  <span className="text-amber-300">
                    {g.rows.length} {g.rows.length === 1 ? "row" : "rows"}
                  </span>
                  , all for the same reason: {g.detail}
                </div>
              )}
              <div className="divide-y divide-zinc-900">
                {g.rows.slice(0, take).map((x, i) => (
                  <div key={x.entry_id || x.charge_id || `${testid}-${gi}-${i}`}
                    className="px-4 py-3 space-y-1" data-testid={`${testid}-row-${gi}-${i}`}>
                    {render(x, { hideDetail: grouped })}
                  </div>
                ))}
              </div>
              {g.rows.length > take && (
                <div className="px-4 py-2 text-[11px] text-zinc-600">
                  {g.rows.length - take} more of these {g.rows.length - take === 1 ? "is" : "are"} not drawn.
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}

// --------------------------------------------------------------------------- //
// The lines. Every figure explainable row by row.
// --------------------------------------------------------------------------- //
function Lines({ lines, currency, leftOut }) {
  const [shown, setShown] = useState(PAGE);
  if (!lines.length) {
    return (
      <div className="bg-zinc-950 border border-zinc-900 rounded-md px-4 py-8 text-sm text-zinc-500" data-testid="billing-run-lines-empty">
        {leftOut
          ? "This run holds no lines, so it bills nothing, and that is not because the month was quiet. Read the rows above: the work is there and none of it could be billed."
          : "This run holds no lines, so it bills nothing, and nothing was left out to make it look that way. No approved, billable work was logged for this client in this month and no contract charge fell due in it."}
      </div>
    );
  }
  const visible = lines.slice(0, shown);
  return (
    <div className="bg-zinc-950 border border-zinc-900 rounded-md overflow-hidden" data-testid="billing-run-lines">
      <div className="px-4 py-2.5 border-b border-zinc-900 flex items-center justify-between gap-3 flex-wrap">
        <div className="text-sm text-zinc-200 flex items-center gap-2">
          <FileSpreadsheet size={15} className="text-zinc-500" /> Lines: {lines.length}
        </div>
        {lines.length > visible.length && (
          <button onClick={() => setShown(lines.length)} data-testid="billing-run-lines-show-all"
            className="text-[11px] text-blue-400 hover:underline">
            Showing the first {visible.length} of {lines.length}. Show all {lines.length}
          </button>
        )}
      </div>
      <table className="w-full text-sm hidden lg:table">
        <thead className="bg-zinc-900/50 border-b border-zinc-900 text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
          <tr>
            <th className="text-left px-4 py-3">Date</th>
            <th className="text-left px-4 py-3">Ticket</th>
            <th className="text-left px-4 py-3">Description</th>
            <th className="text-left px-4 py-3">Minutes</th>
            <th className="text-left px-4 py-3">Rate / hour</th>
            <th className="text-left px-4 py-3">Amount</th>
          </tr>
        </thead>
        <tbody>
          {visible.map((l) => (
            <tr key={l.id} data-testid={`billing-line-${l.id}`} className="border-b border-zinc-900 hover:bg-zinc-900/40">
              <td className="px-4 py-2 font-mono text-zinc-400">{l.occurred_on || "No date recorded"}</td>
              <td className="px-4 py-2">
                {l.kind === "contract"
                  ? <span className="text-[10px] uppercase font-mono tracking-wider text-blue-300">Contract charge</span>
                  : l.ticket_id
                    ? <Link to={`/tickets/${l.ticket_id}`} className="text-zinc-300 hover:text-blue-400">#{l.ticket_number ?? "number unknown"} {l.ticket_title || ""}</Link>
                    : <span className="text-zinc-500">No ticket recorded</span>}
              </td>
              <td className="px-4 py-2 text-zinc-300">{l.description || "No description recorded"}</td>
              {/* ⚠️ A CONTRACT LINE CARRIES NO `minutes` AND NO `rate_minor`,
                  and they are ABSENT rather than zero on purpose: a retainer is
                  a fee with no time in it, not an amount of time priced at
                  nothing. So these cells say so in words rather than printing a
                  0 somebody could divide by, or a dash. */}
              <td className="px-4 py-2 font-mono text-zinc-400">{fmtMinutes(l.minutes) ?? <span className="text-zinc-600 font-sans text-xs">No time in this charge</span>}</td>
              <td className="px-4 py-2 font-mono text-zinc-400">{pesoOr(l.rate_minor, <span className="text-zinc-600 font-sans text-xs">Flat fee, not hourly</span>)}</td>
              <td className="px-4 py-2 font-mono text-zinc-100">{pesoOr(l.amount_minor, "No amount recorded")}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="lg:hidden divide-y divide-zinc-900">
        {visible.map((l) => (
          <div key={l.id} className="p-4 space-y-1" data-testid={`billing-line-card-${l.id}`}>
            <div className="text-zinc-100">{l.description || "No description recorded"}</div>
            <div className="text-[11px] text-zinc-500 font-mono">
              {l.occurred_on || "No date recorded"} · {l.kind === "contract" ? "contract charge" : `ticket #${l.ticket_number ?? "unknown"}`}
            </div>
            <div className="text-[11px] text-zinc-500">
              {fmtMinutes(l.minutes) ?? "No time in this charge"} · {pesoOr(l.rate_minor, "flat fee")}
            </div>
            <div className="text-sm text-zinc-100 font-mono">{pesoOr(l.amount_minor, "No amount recorded")} {l.currency || currency}</div>
          </div>
        ))}
      </div>
      {/* ⚠️ NO TOTAL ROW IS DRAWN HERE, DELIBERATELY. Summing the visible lines
          in the browser would produce a figure that disagrees with the
          server's the moment the list is capped, and totalling all of them
          would be a second computation of money the server already owns. The
          run's total is at the top of this page, from `total_minor`. */}
      <div className="px-4 py-2 text-[11px] text-zinc-600 border-t border-zinc-900">
        Each line carries the amount recorded against it when the run was built. The run total above is the server's figure over all {lines.length} {lines.length === 1 ? "line" : "lines"}, not a sum of the rows drawn here.
      </div>
      {/* Amounts and VAT, in the export's own words. See the export document:
          the platform has no tax flag on work types, client rates, contract
          charges or billing settings, so asserting either answer would be an
          invention about somebody's rate card. */}
      <div className="px-4 py-2 text-[11px] text-zinc-600 border-t border-zinc-900" data-testid="billing-run-vat">
        VAT computed by this system: none. Whether these amounts already include VAT is not recorded by this system, so check the client's rate card before the figure is used on an invoice.
      </div>
    </div>
  );
}
